import { Empty, Flex, Modal, Skeleton } from "antd"
import Search from "antd/es/input/Search"
import Title from "antd/lib/typography/Title"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import InfiniteScroll from "react-infinite-scroll-component"
import { useParams } from "react-router"

import { useAppDispatch } from "../../../../hooks/use-app-dispatch"
import { debounce, serializeData } from "../../../../utils/utils"
import { fetchAlbums, fetchMoreAlbums } from "../../../Album/redux/album.thunks"
import type { AlbumListItem } from "../../../Album/types/album.types"
import { AlbumSelectorCard } from "./sections/ProjectViewAlbums/AlbumCard/AlbumSelectorCard"
import { ALBUM_CARD_WITH } from "./sections/ProjectViewAlbums/album-card-width.ts"

const PAGE_SIZE = 20

interface ProjectFileAddToAlbumModalProps {
  open: boolean
  onClose: () => void
  onSave: (albumId: string) => void
}

export function ProjectFileAddToAlbumModal({
  open,
  onClose,
  onSave
}: ProjectFileAddToAlbumModalProps) {
  const projectId = useParams()?.id as string

  const dispatch = useAppDispatch()

  const [albums, setAlbums] = useState<AlbumListItem[]>([])
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [next, setNext] = useState("")
  const [selectedAlbum, setSelectedAlbum] = useState<string>()
  const [searchQuery, setSearchQuery] = useState("")
  const [page, setPage] = useState(1)

  const handleClose = () => {
    onClose()
    setPage(1)
    setNext("")
    setSearchQuery("")
    setSelectedAlbum("")
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchQuery(value)
    debouncedSearch(value)
  }

  const handleLoadMore = () => {
    const nextPage = page + 1
    setPage(nextPage)
    handleGetAlbums(searchQuery, nextPage)
  }

  const handleGetAlbums = useCallback(
    async (q: string, page: number) => {
      try {
        const filters = { q, page, page_size: PAGE_SIZE }
        const serializeFilters = serializeData(filters || {})
        if (filters.page === 1) {
          setLoading(true)
          const result = await dispatch(
            fetchAlbums({
              project: projectId,
              ...serializeFilters
            })
          ).unwrap()
          setAlbums(result.results || [])
          setNext(result.next || "")
        } else {
          const result = await dispatch(
            fetchMoreAlbums({
              project: projectId,
              ...serializeFilters
            })
          ).unwrap()
          setAlbums((prev) => [...prev, ...(result.results || [])])
          setNext(result.next || "")
        }
      } catch (e) {
        console.error("Failed to fetch albums:", e)
      } finally {
        setLoading(false)
      }
    },
    [projectId, dispatch]
  )

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setPage(1)
        setNext("")
        setSelectedAlbum("")
        void handleGetAlbums(value, 1)
      }, 500),
    [handleGetAlbums]
  )

  const handleSubmit = () => {
    if (!selectedAlbum) return
    try {
      setSubmitting(true)
      onSave(selectedAlbum)
      handleClose()
    } catch (e) {
      console.error("Failed to add attachments:", e)
    } finally {
      setSubmitting(false)
    }
  }

  const onCardClick = (albumId: string) => {
    if (albumId === selectedAlbum) {
      setSelectedAlbum("")
    } else {
      setSelectedAlbum(albumId)
    }
  }

  useEffect(() => {
    if (open) {
      void handleGetAlbums("", 1)
    }
  }, [open])

  return (
    <>
      <Modal
        title={
          <Title level={4} className="px-2 text-left">
            Select Album
          </Title>
        }
        destroyOnHidden
        open={open}
        onCancel={handleClose}
        onOk={handleSubmit}
        cancelText="Cancel"
        confirmLoading={submitting}
        okText={"Save"}
        okButtonProps={{ disabled: !selectedAlbum }}
        width={900}
      >
        <Flex vertical gap={16} className="pl-2 pr-2">
          <Search
            placeholder="Search albums..."
            value={searchQuery}
            onChange={handleSearchChange}
            allowClear
            className="flex-1"
          />
        </Flex>
        <InfiniteScroll
          className="tailwind-scroll mt-2 p-2"
          height={420}
          dataLength={albums.length}
          next={handleLoadMore}
          hasMore={!!next}
          loader={
            <div
              className="grid gap-4"
              style={{
                gridTemplateColumns: `repeat(auto-fill, minmax(${ALBUM_CARD_WITH}px, 1fr))`
              }}
            >
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton.Node
                  key={i}
                  active
                  style={{ width: "100%", height: "120px" }}
                  className="w-full"
                />
              ))}
            </div>
          }
        >
          {!loading && !albums.length && (
            <Empty
              description={
                searchQuery
                  ? "No albums match your search"
                  : "No available albums"
              }
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          )}
          <div
            className="grid gap-4 "
            style={{
              gridTemplateColumns: `repeat(auto-fill, minmax(${ALBUM_CARD_WITH}px, 1fr))`
            }}
          >
            {loading ? (
              <>
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton.Node
                    key={i}
                    active
                    style={{ width: "100%", height: "120px" }}
                    className="w-full"
                  />
                ))}
              </>
            ) : (
              <>
                {albums.map((album) => (
                  <AlbumSelectorCard
                    key={album.id}
                    album={album}
                    isSelected={selectedAlbum === album.id}
                    onClick={onCardClick}
                  />
                ))}
              </>
            )}
          </div>
        </InfiniteScroll>
      </Modal>
    </>
  )
}
